import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'
import ClientContext from 'contexts/ClientContext'
import classNames from 'classnames'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle'
import PageTitle from 'components/common/PageTitle/PageTitle'
import Card from 'components/common/Card/Card'
import DocumentText from 'components/assets/icons/DocumentText'
import ExclamationTriangle from 'components/assets/icons/ExclamationTriangle'
import Textarea from 'components/common/Textarea/Textarea'
import Select from 'components/common/Select/Select'
import Datepicker from 'components/common/Datepicker/Datepicker'
import style from './CircumstancesForm.module.scss'
import { isInputError } from 'helpers/form'
import api from 'helpers/api'

const CircumstancesForm = () => {
  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const [ contract, setContract ] = useState(state?.contract)
  const { color, colabelNeutralDark } = useContext(ClientContext)
  const { claimsPersonConcerned, isSomeoneElse } = state || {}
  const fetched = useRef(null)

  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    criteriaMode: 'all',
    delayError: 500,
    defaultValues: {
      incidentDate: new Date(),
      location: '',
      nature: '',
      circumstance: '',
    },
    mode: 'onTouched',
  })

  useEffect(() => {
    const getContract = async () => {
      fetched.current = language
      try {
        const res = await api.get({ url: `/adhesions/${contract._id}/client` })
        if (res?.adhesion) setContract(res.adhesion)
      } catch (error) {
        console.log('Fetch contracts error')
      }
    }
    if (fetched.current === language) getContract()
  }, [language, contract, fetched])

  const claimNatureOptions = (
    (contract.rateType?.claimNatures && contract.rateType.claimNatures.length > 0) 
      ? contract.rateType.claimNatures 
      : (contract.contractType?.claimNatures && contract.contractType.claimNatures.length > 0)
        ? contract.contractType.claimNatures
        : []
  ).map(item => ({
    label: item.name,
    value: item._id,
    proofOfClaims: item.proofOfClaims
  }));
  claimNatureOptions.push({ label: t('common:other'), value: 'other', proofOfClaims: [] });


  const errorFields = Object.keys(errors)

  const onSubmit = handleSubmit(async data => {
    navigate('../documents', {
      state: {
        ...state,
        step: 4,
        claim: {
          ...state.claim,
          ...data,
          notPersonnalClaimDeclaration: claimsPersonConcerned
            ? !!isSomeoneElse
            : false
        },
        claimNature: claimNatureOptions.find(item => item.value === data.nature.value)
      },
    })
  })

  return (
    <>
      <PageTitle text={t('claim:applySteps.circumstances.title')} />
      <PageSubtitle text={t('claim:applySteps.circumstances.subtitle')} />

      <Card
          className={style.contract}
          inactive={contract.cancelation}
          content={
            <>
              {contract.rateType.dashboardPackageDescription || contract.contractType.dashboardDescription}
              {contract.contractType.displayCardNumber && 
                <ul className={style.cards}>
                  {contract.virtualCard && <li>{t('common:virtualCard')} : {`**** **** **** ${contract.virtualCard.slice(-4)}`}</li>}
                  {contract.physicalCard && <li>{t('common:physicalCard')} : {`**** **** **** ${contract.physicalCard.slice(-4)}`}</li>}
                  {contract.physicalCard2 && <li>{t('common:physicalCard2')} : {`**** **** **** ${contract.physicalCard2.slice(-4)}`}</li>}
                </ul>
              }
            </>
          }
          title={
            contract.rateType?.dashboardPackageName 
              ? <>{contract.rateType.dashboardPackageName} - <i>{contract.idTransaction}</i></>
              : <>{contract.contractType.dashboardName} - <i>{contract.idTransaction}</i></>
          }
          titleIcon={<DocumentText styles={color && {fill: colabelNeutralDark}} />}
        />
        {contract.rateType.PhoneRapatriement && (
          <Card
            className={style.contract}
            inactive={contract.cancelation}
            content={
              <>
                <div>{t('claim:applySteps.circumstances.emergencyRepatriation.line1')}</div>
                <div>{t('claim:applySteps.circumstances.emergencyRepatriation.line2', { number: contract.rateType.PhoneRapatriement })}</div>
              </>
            }
            title={t('claim:applySteps.circumstances.emergencyRepatriation.title')}
            titleIcon={<ExclamationTriangle styles={{fill: '#FFB02F'}} exclamationColor={'#000000'} />}
            
          />
        )}
        

      <form className={style.form} onSubmit={onSubmit}>
        <div className={style.inputs}>
          <div className={classNames(style.datepicker, style.input)}>
            <div className="input-label" style={color && {color: colabelNeutralDark}}>{t('claim:applySteps.circumstances.incidentDate')}</div>
            <Controller
              control={control}
              name="incidentDate"
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  className={style.input}
                  isTimepickerShown
                  onChange={onChange}
                  placeholder={t('claim:applySteps.circumstances.incidentDate')}
                  register={register('incidentDate', { required: true })}
                  value={value}
                />
              )}
            />
          </div>
          <Input
            className={style.input}
            error={isInputError(errorFields, 'location')}
            label={t('claim:item.location')}
            placeholder={t('claim:item.location')}
            register={register('location', { required: true })}
          />
          <Controller
            control={control}
            name="nature"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Select
                className={classNames(style.select, style.input)}
                error={isInputError(errorFields, 'nature')}
                label={t('claim:item.nature')}
                onChange={val => onChange(val)}
                options={claimNatureOptions}
                placeholder={t('common:select.placeholder')}
                value={claimNatureOptions.find(item => item.value === value) || value.label}
                styles={{ indicatorContainer: { paddingRight: 8 } }}
              />
            )}
          />
        </div>

        <Textarea
          className={style.textarea}
          error={isInputError(errorFields, 'circumstance')}
          label={t('claim:applySteps.circumstances.circumstances.label')}
          placeholder={t('claim:applySteps.circumstances.circumstances.placeholder')}
          register={register('circumstance', { required: true })}
        />

        

        <Button
          className={style.button}
          label={t('claim:submit')}
          onClick={handleSubmit}
          type="primary"
          disabled={!isValid}
        />
      </form>
    </>
  )
}

export default CircumstancesForm
